import React, { useState } from 'react';
import { app } from './firebase';
import { getFirestore, updateDoc, doc, arrayUnion, deleteDoc, setDoc } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getApp } from 'firebase/app'; 

const db = getFirestore(app);

export default function ActiveContractsView({ contracts, consultants, consultantDb, loading, logAction, isReadOnly }) {
  const [selectedContract, setSelectedContract] = useState(null);
  const [actionType, setActionType] = useState(null); 
  const [actionData, setActionData] = useState({});
  const [processing, setProcessing] = useState(false);
  
  // NEW: State for viewing logs
  const [logsContract, setLogsContract] = useState(null);

  // --- HEALTH CHECK-IN LOGIC ---
  const handleAddCheckIn = async (contract) => {
      if(isReadOnly) return;
      const note = prompt("Enter notes for this check-in:");
      if(!note) return;

      const newCheckIn = { 
          date: new Date().toISOString(), 
          note, 
          type: 'Health Check',
          loggedBy: 'Admin' 
      };

      try {
          await updateDoc(doc(db, "active_contracts", contract.id), {
              checkIns: arrayUnion(newCheckIn),
              lastCheckInDate: newCheckIn.date
          });
          logAction("Log Check-in", `Logged check-in for ${contract.consultantName}`);
      } catch (err) {
          console.error(err);
          alert("Failed to log check-in.");
      }
  };

  // --- RENEWAL PIPELINE LOGIC ---
  const handleRenewalStage = async (contract, stage) => {
      if(isReadOnly) return;
      try {
          await updateDoc(doc(db, "active_contracts", contract.id), { renewalStage: stage });
      } catch(err) {
          console.error(err);
      }
  };

  const getRenewalBadge = (stage) => {
      const map = { 
          'Not Started': 'bg-secondary', 
          'Contacted': 'bg-info', 
          'Negotiating': 'bg-warning', 
          'Signed': 'bg-success' 
      };
      return map[stage] || 'bg-secondary';
  };

  // --- HELPER: Pre-fill Consultant Info ---
  const handleEditInfoClick = (contract) => {
      setSelectedContract(contract);
      setActionType('EditConsultant');

      const activeRecord = consultants.find(c => c.id === contract.consultantId) || {};
      const dbRecord = consultantDb.find(c => c.id === contract.consultantId) || {};

      const initialData = {
          email: dbRecord.email || activeRecord.email || '',
          phone: dbRecord.phone || activeRecord.phone || '',
          location: dbRecord.city || activeRecord.Location || ''
      };

      setActionData(initialData);
  };

  // --- PORTAL ONBOARDING ---
  const handleOnboardConsultant = async (contract) => {
      if(isReadOnly) return;
      if (!window.confirm(`Generate portal credentials for ${contract.consultantName}?\n\nThis will create a user account and email login details.`)) return;
      setProcessing(true);
      
      try {
          const functions = getFunctions(getApp(), 'europe-west1');
          const createPortalUser = httpsCallable(functions, 'createPortalUser');
          
          let email = '';
          const dbRecord = consultantDb.find(c => c.id === contract.consultantId);
          if (dbRecord && dbRecord.email) {
              email = dbRecord.email;
          } 
          
          if (!email) {
              const legacyRecord = consultants.find(c => c.id === contract.consultantId);
              if (legacyRecord && legacyRecord.email) email = legacyRecord.email;
          }

          if(!email) {
              alert("Consultant email not found. Please click 'Edit Info' to add an email address first.");
              setProcessing(false);
              return;
          }

          const result = await createPortalUser({
              email: email,
              name: contract.consultantName,
              consultantId: contract.consultantId
          });

          if(result.data.success) {
              alert(`Success! Credentials sent to ${email}.`);
              logAction("Onboard Consultant", `Created portal user for ${contract.consultantName}`);
          } else {
              alert("Error creating user: " + result.data.error);
          }
      } catch (err) {
          console.error(err);
          if (err.code === 'functions/already-exists') {
              alert("A user account already exists for this email. If they lost their credentials, please use 'Send Password Reset' instead.");
          } else {
              alert("Failed to onboard: " + err.message);
          }
      } finally {
          setProcessing(false);
      }
  };

  // --- PASSWORD RESET ---
  const handleResetPassword = async (contract) => {
      if(isReadOnly) return;
      setProcessing(true);
      try {
          const functions = getFunctions(getApp(), 'europe-west1');
          const resetFn = httpsCallable(functions, 'resetPortalPassword');
          
          let email = '';
          const dbRecord = consultantDb.find(c => c.id === contract.consultantId);
          if (dbRecord) email = dbRecord.email;
          
          if(email) {
              await resetFn({ email });
              alert(`Reset instructions sent to ${email}.`);
              logAction("Reset Password", `Sent reset instructions to ${contract.consultantName}`);
          } else {
              alert("No email address found for this consultant profile.");
          }
      } catch(err) {
          console.error(err);
          if (err.code === 'functions/not-found') {
              alert("There is no user created yet for this consultant. Please use 'Create User & Send Credentials' first.");
          } else {
              alert("Error sending reset email: " + err.message);
          }
      } finally {
          setProcessing(false);
      }
  };

  // --- GENERAL UPDATES ---
  const handleUpdate = async () => {
      if (!selectedContract) return;
      const ref = doc(db, "active_contracts", selectedContract.id);
      let updates = {};
      let logMsg = '';

      try {
          if (actionType === 'Renew') {
              updates = { 
                  endDate: actionData.newEndDate, 
                  status: 'Active', 
                  renewalStage: 'Signed', 
                  history: arrayUnion({ type: 'Renewal', date: new Date().toISOString(), details: `Extended to ${actionData.newEndDate}` }) 
              };
              logMsg = `Renewed contract for ${selectedContract.consultantName}`;

              if (selectedContract.consultantId) {
                  const dbRef = doc(db, "consultant_db", selectedContract.consultantId);
                  await setDoc(dbRef, { 
                      availabilityDate: actionData.newEndDate,
                      status: 'Busy'
                  }, { merge: true });
              }

          } else if (actionType === 'RateChange') {
              updates = { 
                  dayRate: actionData.newSellRate, 
                  costRate: actionData.newBuyRate, 
                  history: arrayUnion({ 
                      type: 'Rate Change', 
                      date: new Date().toISOString(), 
                      details: `Sell: ${selectedContract.dayRate}→${actionData.newSellRate}, Buy: ${selectedContract.costRate}→${actionData.newBuyRate}` 
                  }) 
              };
              logMsg = `Updated rates for ${selectedContract.consultantName}`;

          } else if (actionType === 'EditConsultant') {
              if (selectedContract.consultantId) {
                  const consRef = doc(db, "consultants", selectedContract.consultantId);
                  const dbRef = doc(db, "consultant_db", selectedContract.consultantId);
                  
                  const payload = {};
                  if(actionData.email) payload.email = actionData.email;
                  if(actionData.phone) payload.phone = actionData.phone;
                  if(actionData.location) {
                      payload.Location = actionData.location; 
                      payload.city = actionData.location;     
                  }

                  await setDoc(consRef, payload, { merge: true });
                  await setDoc(dbRef, payload, { merge: true });
                  
                  alert("Consultant contact info updated.");
                  logMsg = `Updated info for ${selectedContract.consultantName}`;
                  
                  setSelectedContract(null); 
                  setActionType(null); 
                  if (logMsg) logAction("Update Consultant", logMsg);
                  return;
              }

          } else if (actionType === 'Manage') {
              if (actionData.subAction === 'Archive') {
                  updates = { status: 'Archived' };
                  logMsg = `Archived contract for ${selectedContract.consultantName}`;
              } else if (actionData.subAction === 'Delete') {
                  if (!window.confirm("Permanently delete this contract?")) return;
                  await deleteDoc(ref);
                  logAction("Delete Contract", `Deleted contract for ${selectedContract.consultantName}`);
                  setSelectedContract(null);
                  setActionType(null);
                  return; 
              } else if (actionData.subAction === 'ChangeStatus') {
                  updates = { status: actionData.newStatus };
                  logMsg = `Changed status to ${actionData.newStatus}`;
              }
          }

          if (Object.keys(updates).length > 0) {
              await updateDoc(ref, updates);
              if (logMsg) logAction("Update Contract", logMsg);
          }
          
          setActionType(null);
          setSelectedContract(null); 
      } catch (err) {
          console.error("Error updating contract:", err);
          alert("Failed to update contract.");
      }
  };

  const getStatusBadge = (status) => {
      switch(status) {
          case 'Active': return 'bg-success';
          case 'Renewed': return 'bg-primary';
          case 'Pending Renewal': return 'bg-warning text-dark';
          case 'Terminating': return 'bg-danger';
          case 'Archived': return 'bg-secondary';
          default: return 'bg-secondary';
      }
  };

  const calculateProfit = (c) => {
      const days = 20; 
      const sell = parseFloat(c.dayRate || 0); 
      const buy = parseFloat(c.costRate || 0); 
      const revenue = sell * days;
      const cost = buy * days;
      const margin = revenue - cost;
      const marginPercent = revenue > 0 ? (margin / revenue) * 100 : 0;
      return { revenue, margin, marginPercent, buy, sell };
  };

  // --- RENDER ---
  return (
    <div className="container-fluid p-0">
      <div className="mb-4">
        <h2 className="fw-bold text-main">Active Contracts</h2>
        <p className="text-muted-theme">Manage engagements, renewals, and consultant relations.</p>
      </div>

      {loading ? <div className="text-main">Loading...</div> : (
        <div className="row g-4">
            {contracts.map(c => {
                const financials = calculateProfit(c);
                const renewalStages = ['Not Started', 'Contacted', 'Negotiating', 'Signed'];
                const currentRenewalIdx = renewalStages.indexOf(c.renewalStage || 'Not Started');
                const lastCheckIn = c.lastCheckInDate ? new Date(c.lastCheckInDate).toLocaleDateString() : 'Never';
                
                const lastDate = c.lastCheckInDate ? new Date(c.lastCheckInDate) : new Date(0);
                const daysSinceCheckIn = Math.floor((new Date() - lastDate) / (1000 * 60 * 60 * 24));
                const needsCheckIn = daysSinceCheckIn > 30;

                return (
                    <div key={c.id} className="col-lg-6">
                        <div className={`card bg-card border-theme h-100 shadow-sm ${c.status === 'Archived' ? 'opacity-50' : ''}`}>
                            <div className="card-header border-theme d-flex justify-content-between align-items-center bg-transparent">
                                <div>
                                    <h5 className="mb-0 text-main fw-bold">{c.consultantName}</h5>
                                    <small className="text-muted-theme">{c.jobTitle} @ {c.client}</small>
                                </div>
                                <span className={`badge ${getStatusBadge(c.status)}`}>{c.status}</span>
                            </div>
                            
                            <div className="card-body">
                                
                                {/* 1. RENEWAL PIPELINE */}
                                <div className="mb-4">
                                    <div className="d-flex justify-content-between mb-1">
                                        <small className="text-muted-theme">Renewal Progress (Click to Advance)</small>
                                        <small className={`fw-bold ${c.renewalStage === 'Signed' ? 'text-success' : 'text-main'}`}>{c.renewalStage || 'Not Started'}</small>
                                    </div>
                                    <div className="progress bg-input border border-theme" style={{height: '10px', cursor: isReadOnly ? 'default' : 'pointer'}} 
                                         onClick={() => {
                                             if(isReadOnly) return;
                                             const nextStage = renewalStages[(currentRenewalIdx + 1) % renewalStages.length];
                                             handleRenewalStage(c, nextStage);
                                         }}
                                         title="Click to advance stage: Not Started -> Contacted -> Negotiating -> Signed"
                                    >
                                        <div className={`progress-bar ${getRenewalBadge(c.renewalStage || 'Not Started')}`} style={{width: `${((currentRenewalIdx+1)/renewalStages.length)*100}%`}}></div>
                                    </div>
                                </div>

                                {/* 2. HEALTH CHECK-IN */}
                                <div className={`d-flex justify-content-between align-items-center mb-4 p-3 rounded border border-theme ${needsCheckIn ? 'bg-warning bg-opacity-10' : 'bg-input'}`}>
                                    <div>
                                        <div className="d-flex align-items-center gap-2">
                                            <small className="text-muted-theme">Last Check-in:</small>
                                            {needsCheckIn && <i className="bi bi-exclamation-circle-fill text-warning" title="Overdue (>30 days)"></i>}
                                        </div>
                                        <div className="fw-bold text-main">{lastCheckIn}</div>
                                    </div>
                                    <div className="d-flex gap-2">
                                        <button className="btn btn-sm btn-outline-info" onClick={() => setLogsContract(c)}>
                                            <i className="bi bi-clock-history"></i> History
                                        </button>
                                        {!isReadOnly && (
                                            <button className="btn btn-sm btn-outline-primary" onClick={() => handleAddCheckIn(c)}>
                                                <i className="bi bi-chat-left-text me-1"></i> Log
                                            </button>
                                        )}
                                    </div>
                                </div>

                                {/* 3. CONTRACT DETAILS */}
                                <div className="row g-3 mb-4 pt-3 border-top border-theme">
                                    <div className="col-6">
                                        <small className="text-muted-theme d-block">Duration</small>
                                        <span className="text-main fw-bold">{c.startDate} <i className="bi bi-arrow-right small mx-1"></i> {c.endDate}</span>
                                    </div>
                                    <div className="col-6">
                                        <small className="text-muted-theme d-block">Est. Monthly Rev</small>
                                        <span className="text-main">€{financials.revenue.toLocaleString()}</span>
                                    </div>
                                    <div className="col-6">
                                        <small className="text-muted-theme d-block">Rates (Sell / Buy)</small>
                                        <span className="text-success fw-bold">€{financials.sell}</span> <span className="text-muted-theme small">/ €{financials.buy}</span>
                                    </div>
                                    <div className="col-6">
                                        <small className="text-muted-theme d-block">Est. Margin</small>
                                        <span className="text-info fw-bold">€{financials.margin.toLocaleString()} ({financials.marginPercent.toFixed(1)}%)</span>
                                    </div>
                                </div>

                                {/* 4. ACTIONS */}
                                <div className="d-flex gap-2 flex-wrap">
                                    <button className="btn btn-sm btn-outline-primary" onClick={() => {setSelectedContract(c); setActionType('Renew');}}>Renew</button>
                                    <button className="btn btn-sm btn-outline-warning" onClick={() => {setSelectedContract(c); setActionType('RateChange');}}>Rates</button>
                                    <button className="btn btn-sm btn-outline-info" onClick={() => handleEditInfoClick(c)}>Edit Info</button>
                                    
                                    <div className="dropdown">
                                        <button className="btn btn-sm btn-secondary dropdown-toggle" type="button" data-bs-toggle="dropdown" aria-expanded="false" disabled={processing}>
                                            Portal
                                        </button>
                                        <ul className="dropdown-menu">
                                            <li><button className="dropdown-item" onClick={() => handleOnboardConsultant(c)}>Create User & Send Credentials</button></li>
                                            <li><button className="dropdown-item" onClick={() => handleResetPassword(c)}>Send Password Reset</button></li>
                                        </ul>
                                    </div>

                                    {!isReadOnly && (
                                        <button className="btn btn-sm btn-outline-danger ms-auto" onClick={() => {setSelectedContract(c); setActionType('Manage');}}><i className="bi bi-gear"></i></button>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                );
            })}
        </div>
      )}

      {/* CHECK-IN HISTORY MODAL */}
      {logsContract && (
          <div className="modal show d-block" style={{backgroundColor: 'rgba(0,0,0,0.5)'}}>
              <div className="modal-dialog modal-dialog-centered modal-dialog-scrollable">
                  <div className="modal-content">
                      <div className="modal-header border-theme">
                          <h5 className="modal-title text-main">Check-in History</h5>
                          <button className="btn-close" onClick={() => setLogsContract(null)}></button>
                      </div>
                      <div className="modal-body">
                          <h6 className="mb-3 text-muted-theme">{logsContract.consultantName}</h6>
                          {logsContract.checkIns && logsContract.checkIns.length > 0 ? (
                              <div className="list-group">
                                  {[...logsContract.checkIns].reverse().map((log, idx) => (
                                      <div key={idx} className="list-group-item bg-card border-theme">
                                          <div className="d-flex justify-content-between mb-1">
                                              <small className="fw-bold text-main">{new Date(log.date).toLocaleDateString()} {new Date(log.date).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}</small>
                                              <small className="text-muted-theme">{log.loggedBy}</small>
                                          </div>
                                          <p className="mb-0 text-main small">{log.note}</p>
                                      </div>
                                  ))}
                              </div>
                          ) : (
                              <p className="text-center text-muted-theme py-4">No check-ins logged yet.</p>
                          )}
                      </div>
                      <div className="modal-footer border-theme">
                          <button className="btn btn-secondary" onClick={() => setLogsContract(null)}>Close</button>
                      </div>
                  </div>
              </div>
          </div>
      )}

      {/* ACTION MODAL */}
      {selectedContract && (
          <div className="modal show d-block" style={{backgroundColor: 'rgba(0,0,0,0.5)'}}>
              <div className="modal-dialog modal-dialog-centered">
                  <div className="modal-content">
                      <div className="modal-header">
                          <h5 className="modal-title text-main">
                              {actionType === 'Manage' ? 'Manage Contract' :
                               actionType === 'EditConsultant' ? `Edit ${selectedContract.consultantName}` :
                               `Update: ${selectedContract.consultantName}`}
                          </h5>
                          <button className="btn-close" onClick={() => {setSelectedContract(null); setActionType(null);}}></button>
                      </div>
                      <div className="modal-body">
                          
                          {/* EDIT CONSULTANT INFO */}
                          {actionType === 'EditConsultant' && (
                              <div className="d-flex flex-column gap-3">
                                  <p className="small text-muted-theme">Updates here are synced to both Active and Candidate databases.</p>
                                  <div>
                                      <label className="form-label small text-muted-theme">Email (Required for Portal)</label>
                                      <input className="form-control" placeholder="john.doe@example.com" value={actionData.email || ''} onChange={e => setActionData({...actionData, email: e.target.value})} />
                                  </div>
                                  <div>
                                      <label className="form-label small text-muted-theme">Phone</label>
                                      <input className="form-control" placeholder="+32 477 ..." value={actionData.phone || ''} onChange={e => setActionData({...actionData, phone: e.target.value})} />
                                  </div>
                                  <div>
                                      <label className="form-label small text-muted-theme">Location</label>
                                      <input className="form-control" placeholder="City, Country" value={actionData.location || ''} onChange={e => setActionData({...actionData, location: e.target.value})} />
                                  </div>
                              </div>
                          )}

                          {/* RENEW FORM */}
                          {actionType === 'Renew' && (
                              <div>
                                  <p className="small text-muted-theme">Renewing will extend the contract and automatically update the consultant's availability date.</p>
                                  <label className="form-label text-muted-theme">New End Date</label>
                                  <input type="date" className="form-control" defaultValue={selectedContract.endDate} onChange={e => setActionData({...actionData, newEndDate: e.target.value})} />
                              </div>
                          )}

                          {/* RATES FORM */}
                          {actionType === 'RateChange' && (
                              <div className="row g-3">
                                  <div className="col-12"><p className="small text-muted-theme">Adjust rates for margin calculation.</p></div>
                                  <div className="col-6">
                                      <label className="form-label text-muted-theme">Sell Rate (Client) €</label>
                                      <input type="number" className="form-control" defaultValue={selectedContract.dayRate} onChange={e => setActionData({...actionData, newSellRate: e.target.value, newBuyRate: actionData.newBuyRate || selectedContract.costRate})} />
                                  </div>
                                  <div className="col-6">
                                      <label className="form-label text-muted-theme">Buy Rate (Consultant) €</label>
                                      <input type="number" className="form-control" defaultValue={selectedContract.costRate} onChange={e => setActionData({...actionData, newBuyRate: e.target.value, newSellRate: actionData.newSellRate || selectedContract.dayRate})} />
                                  </div>
                              </div>
                          )}

                          {/* SUPER ADMIN MANAGE FORM */}
                          {actionType === 'Manage' && (
                              <div className="d-flex flex-column gap-2">
                                  <div className="mb-3">
                                      <label className="form-label text-muted-theme">Change Status</label>
                                      <select className="form-select" defaultValue={selectedContract.status} onChange={(e) => {setActionData({subAction: 'ChangeStatus', newStatus: e.target.value})}}>
                                          <option>Active</option>
                                          <option>Pending Renewal</option>
                                          <option>Terminating</option>
                                          <option>Ended</option>
                                      </select>
                                      <button className="btn btn-sm btn-primary mt-2 w-100" onClick={handleUpdate}>Save Status</button>
                                  </div>
                                  <hr className="border-theme"/>
                                  <button className="btn btn-outline-secondary w-100 text-start" onClick={() => {setActionData({subAction: 'Archive'}); handleUpdate();}}>
                                      <i className="bi bi-archive me-2"></i> Archive Contract
                                  </button>
                                  <button className="btn btn-outline-danger w-100 text-start" onClick={() => {setActionData({subAction: 'Delete'}); handleUpdate();}}>
                                      <i className="bi bi-trash me-2"></i> Delete Contract Permanently
                                  </button>
                              </div>
                          )}
                      </div>
                      
                      {/* FOOTER FOR STANDARD ACTIONS */}
                      {(actionType === 'Renew' || actionType === 'RateChange' || actionType === 'EditConsultant') && (
                          <div className="modal-footer border-theme">
                              <button className="btn btn-primary w-100" onClick={handleUpdate}>Save Changes</button>
                          </div>
                      )}
                  </div>
              </div>
          </div>
      )}
    </div>
  );
}